import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { listasComprasApi, notasFiscaisApi } from '../../api/endpoints'
import { Button } from '../../components/ui/Button'
import { Select } from '../../components/ui/Input'
import { LoadingState } from '../../components/ui/EmptyState'
import { Modal } from '../../components/ui/Modal'
import { useToast } from '../../components/ui/Toast'
import { StatusNota } from '../../lib/enums'
import { cn, formatDate, formatMoney } from '../../lib/utils'
import type { ConciliacaoLista } from '../../types'

type Acao = 'manter' | 'descartar'

/** Escolhe uma nota, mostra o que bateu com a lista e o que faltou, e só grava depois de o usuário confirmar. */
export function ConciliarModal({ listaId, onClose, onAplicado }: { listaId: string; onClose: () => void; onAplicado: () => void }) {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const toast = useToast()
  const [notaId, setNotaId] = useState('')
  const [proposta, setProposta] = useState<ConciliacaoLista | null>(null)
  const [baixas, setBaixas] = useState<Set<string>>(new Set())
  const [faltas, setFaltas] = useState<Record<string, Acao>>({})

  const { data: page, isLoading } = useQuery({ queryKey: ['notas-fiscais'], queryFn: () => notasFiscaisApi.list() })
  const notas = (page?.itens ?? []).filter((n) => n.status !== StatusNota.Pendente && n.itens.length > 0)

  const conciliar = useMutation({
    mutationFn: () => listasComprasApi.conciliarNota(listaId, notaId),
    onSuccess: (c) => {
      setProposta(c)
      setBaixas(new Set(c.baixas.map((b) => b.itemListaId)))
      setFaltas(Object.fromEntries(c.faltas.map((f) => [f.itemListaId, 'manter' as Acao])))
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const aplicar = useMutation({
    mutationFn: () => listasComprasApi.aplicarConciliacao(listaId, {
      notaId,
      baixas: [...baixas],
      faltas: Object.entries(faltas).map(([itemListaId, acao]) => ({ itemListaId, acao })),
    }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['listas-compras'] })
      onAplicado()
      toast.success(r.listaNovaId ? 'Baixas aplicadas. O que ficou foi para uma nova lista.' : 'Baixas aplicadas.')
      onClose()
      if (r.listaNovaId) navigate(`/listas/${r.listaNovaId}`)
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const alternar = (id: string) => setBaixas((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n })
  const todas = (acao: Acao) => setFaltas((f) => Object.fromEntries(Object.keys(f).map((k) => [k, acao])))

  return (
    <Modal
      open onClose={onClose} title="Conciliar com uma nota" size="lg"
      footer={proposta
        ? <><Button variant="light" onClick={() => setProposta(null)}>Voltar</Button><Button icon="ri-check-line" loading={aplicar.isPending} onClick={() => aplicar.mutate()}>Confirmar</Button></>
        : <><Button variant="light" onClick={onClose}>Cancelar</Button><Button icon="ri-links-line" disabled={!notaId} loading={conciliar.isPending} onClick={() => conciliar.mutate()}>Comparar com a lista</Button></>}
    >
      {!proposta ? (
        isLoading ? <LoadingState /> : (
          <div className="space-y-3">
            <p className="text-13 text-muted">Escolha a nota da compra. O Walleon propõe quais itens da lista foram comprados; nada é gravado até você confirmar.</p>
            <Select label="Nota fiscal" value={notaId} onChange={(e) => setNotaId(e.target.value)}>
              <option value="">Escolha a nota...</option>
              {notas.map((n) => <option key={n.id} value={n.id}>{formatDate(n.dataEmissao)} · {n.apelido ?? n.nomeEmitente ?? 'Emitente não informado'} · {formatMoney(n.valorTotal)}</option>)}
            </Select>
          </div>
        )
      ) : (
        <div className="space-y-5">
          <section className="space-y-2">
            <p className="text-13 font-medium text-white">Dar baixa ({proposta.baixas.length} {proposta.baixas.length === 1 ? 'item bateu' : 'itens bateram'})</p>
            {proposta.baixas.length === 0 && <p className="text-13 text-muted">Nenhum item da lista bateu com esta nota.</p>}
            {proposta.baixas.map((b) => (
              <label key={b.itemListaId} className="flex cursor-pointer items-start gap-3 rounded bg-surface2 px-3 py-2">
                <input type="checkbox" className="mt-1 h-4 w-4 accent-primary" checked={baixas.has(b.itemListaId)} onChange={() => alternar(b.itemListaId)} />
                <span className="min-w-0 flex-1">
                  <span className="block text-white">
                    {b.nomeLista}
                    {(!b.completa || b.quantidade > 1) && <span className="text-muted"> · {b.quantidade} {b.completa ? 'unidades' : 'parcial'}</span>}
                  </span>
                  <span className="block text-2xs text-muted">Nota: {b.descricaoNota}</span>
                </span>
                <span className="text-13 text-white">{formatMoney(b.valor)}</span>
              </label>
            ))}
          </section>

          {proposta.faltas.length > 0 && (
            <section className="space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-13 font-medium text-white">Faltaram {proposta.faltas.length} {proposta.faltas.length === 1 ? 'item' : 'itens'} da lista</p>
                <div className="flex gap-2">
                  <Button size="sm" variant="light" onClick={() => todas('manter')}>Manter todos</Button>
                  <Button size="sm" variant="light" onClick={() => todas('descartar')}>Descartar todos</Button>
                </div>
              </div>
              {proposta.faltas.map((f) => (
                <div key={f.itemListaId} className="flex flex-wrap items-center justify-between gap-2 rounded bg-surface2 px-3 py-2">
                  <span className="text-white">
                    {f.quantidadeFaltante < f.quantidade ? <>faltam {f.quantidadeFaltante} de {f.nome}</> : f.nome}
                    <span className="text-muted"> · {f.quantidade}</span>
                  </span>
                  <div className="flex gap-1.5">
                    {(['manter', 'descartar'] as const).map((a) => (
                      <button
                        key={a} type="button" aria-pressed={faltas[f.itemListaId] === a}
                        onClick={() => setFaltas((x) => ({ ...x, [f.itemListaId]: a }))}
                        className={cn('rounded border px-3 py-1 text-13 transition-colors', faltas[f.itemListaId] === a ? 'border-primary bg-primary/10 text-primary' : 'border-border/70 text-muted hover:text-white')}
                      >{a === 'manter' ? 'Manter para a próxima' : 'Descartar'}</button>
                    ))}
                  </div>
                </div>
              ))}
              <p className="text-2xs text-muted">Manter leva só a quantidade que faltou para uma nova lista, a próxima compra; descartar descarta só o faltante (o que já foi comprado fica). Nada é apagado sem a sua escolha.</p>
            </section>
          )}
        </div>
      )}
    </Modal>
  )
}
